import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { NickLogo, PayoffShine } from "@/components/BrandLogo";

const STORAGE_KEY = "sbp-splash-seen";

const CARDS = [
  { label: "Deducibile 100%", sub: "TAEG 0% · 3 rate PayPal", href: "https://www.schedabusiness.it/#deducibile", color: "#4285F4", external: true },
  { label: "Start", sub: "Pacchetto base assistenza", href: "/start", color: "#EA4335" },
  { label: "Plus", sub: "Assistenza completa + bonus", href: "/plus", color: "#FBBC04", dark: true },
  { label: "Virtual Tour", sub: "499€ · Timewarp IN REGALO", href: "/deducibile100", color: "#0D0D0D", gradientBorder: true },
  { label: "Rooftop", sub: "Premium · tutto incluso", href: "/rooftop", color: "#34A853" },
  { label: "Formazione .it", sub: "Corsi 100% detraibili", href: "https://www.schedabusiness.it", color: "#EA4335", external: true },
];

const IntroSplash = ({ onComplete }) => {
  const [phase, setPhase] = useState("init");
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) {
      onComplete?.();
      return;
    }
    const t1 = setTimeout(() => setPhase("logo"), 200);
    const t2 = setTimeout(() => setPhase("cards"), 1200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onComplete]);

  const handleSkip = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    onComplete?.();
  };

  const handleCard = (card) => {
    localStorage.setItem(STORAGE_KEY, "1");
    onComplete?.();
    if (card.external) {
      window.open(card.href, "_blank");
    } else {
      navigate(card.href);
    }
  };

  if (phase === "init") return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-[#0a0e17] flex flex-col items-center justify-center overflow-auto" data-testid="intro-splash">
      <style>{`
        @keyframes payoff-wave {
          0%, 100% { transform: translateY(0); text-shadow: 0 0 8px rgba(255,255,255,0.4), 0 0 16px rgba(255,255,255,0.2); }
          50% { transform: translateY(-4px); text-shadow: 0 0 16px rgba(255,255,255,0.8), 0 0 32px rgba(255,255,255,0.4); }
        }
        .payoff-wave-letter { animation: payoff-wave 2.5s ease-in-out infinite; }
      `}</style>
      {/* Skip */}
      <button
        onClick={handleSkip}
        className="absolute top-4 right-4 text-gray-500 hover:text-white text-sm font-medium px-4 py-2 rounded-full border border-white/10 hover:border-white/30 transition-colors z-10"
        data-testid="intro-skip"
      >
        Salta
      </button>

      <div className="w-full max-w-3xl mx-auto px-4 flex flex-col items-center gap-8 py-12">
        {/* NickLogo + Payoff */}
        <div
          className="flex flex-col items-center gap-3 transition-all duration-700"
          style={{
            opacity: phase !== "init" ? 1 : 0,
            transform: phase !== "init" ? "translateY(0) scale(1)" : "translateY(20px) scale(0.9)",
          }}
        >
          {/* Logo schedabusiness.com + S.com Cares */}
          <div className="flex flex-col items-center gap-2">
            <span className="text-2xl sm:text-3xl font-black tracking-tight leading-none" style={{ fontFamily: "'Poppins', sans-serif", background: 'linear-gradient(135deg, #D4AF37 0%, #F5E6A3 40%, #D4AF37 60%, #B8942E 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              schedabusiness.com
            </span>
            <span className="text-[10px] font-bold italic text-center text-white/70 tracking-wide" style={{ fontFamily: "'Poppins', sans-serif", textShadow: '0 0 8px rgba(255,255,255,0.3)' }}>
              se non ti trovano, non esisti
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <NickLogo size={32} showPayoff={false} />
            <span className="text-xl font-black italic" style={{ fontFamily: "'Poppins', sans-serif", background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37, #B8942E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Cares</span>
          </div>
        </div>

        {/* 6 CTA Cards */}
        <div
          className="w-full transition-all duration-700"
          style={{
            opacity: phase === "cards" ? 1 : 0,
            transform: phase === "cards" ? "translateY(0)" : "translateY(30px)",
          }}
        >
          <p className="text-center text-gray-500 text-xs sm:text-sm mb-4">the best of schedabusiness.com</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            {CARDS.map((card, i) => {
              if (card.gradientBorder) {
                return (
                  <button
                    key={i}
                    onClick={() => handleCard(card)}
                    className="group relative rounded-2xl p-[2px] text-center transition-all duration-300 hover:scale-105 hover:-translate-y-1 cursor-pointer overflow-hidden"
                    style={{ animationDelay: `${i * 100}ms` }}
                    data-testid={`intro-card-${i}`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853] opacity-70 group-hover:opacity-100 transition-opacity rounded-2xl"></div>
                    <div className="relative bg-[#0a0e17] rounded-[14px] p-4 sm:p-5 h-full">
                      <div className="w-10 h-10 mx-auto mb-3 rounded-xl flex items-center justify-center shadow-lg bg-gradient-to-r from-[#4285F4] via-[#FBBC04] to-[#34A853]">
                        <span className="text-sm font-black text-white">{i + 1}</span>
                      </div>
                      <h3 className="text-white text-sm sm:text-base font-bold leading-tight mb-1">{card.label}</h3>
                      <p className="text-gray-500 text-[10px] sm:text-xs leading-tight">{card.sub}</p>
                    </div>
                  </button>
                );
              }
              return (
              <button
                key={i}
                onClick={() => handleCard(card)}
                className="group relative rounded-2xl p-4 sm:p-5 text-center transition-all duration-300 hover:scale-105 hover:-translate-y-1 cursor-pointer"
                style={{
                  background: `linear-gradient(145deg, ${card.color}18, ${card.color}08)`,
                  border: `1.5px solid ${card.color}44`,
                  boxShadow: `inset 0 1px 0 ${card.color}11`,
                  animationDelay: `${i * 100}ms`,
                }}
                data-testid={`intro-card-${i}`}
              >
                <div
                  className="w-10 h-10 mx-auto mb-3 rounded-xl flex items-center justify-center shadow-lg"
                  style={{
                    background: card.color,
                    boxShadow: `0 4px 15px ${card.color}44`,
                  }}
                >
                  <span className={`text-sm font-black ${card.dark ? "text-gray-900" : "text-white"}`}>{i + 1}</span>
                </div>
                <h3 className="text-white text-sm sm:text-base font-bold leading-tight mb-1">{card.label}</h3>
                <p className="text-gray-500 text-[10px] sm:text-xs leading-tight">{card.sub}</p>
              </button>
              );
            })}
          </div>
        </div>

        {/* Enter site button */}
        {phase === "cards" && (
          <button
            onClick={handleSkip}
            className="mt-2 text-gray-400 hover:text-white text-sm font-bold flex items-center gap-2 transition-colors"
            data-testid="intro-enter"
          >
            Entra nel sito
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default IntroSplash;
